import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { influencerService } from '../services/influencerService';
import { resolveModuleAccess, type ModuleAccess } from '../lib/moduleAccess';
import type { Influencer } from '../types/influencer';
import { LayoutGrid, FileText, Users, LogOut, Search, X } from 'lucide-react';
import { RefreshCw } from 'lucide-react';

export default function AppsLauncher() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [access, setAccess] = useState<ModuleAccess | null>(null);
  const [loading, setLoading] = useState(true);
  const [creatorPickerOpen, setCreatorPickerOpen] = useState(false);
  const [creators, setCreators] = useState<Influencer[] | null>(null);
  const [creatorSearch, setCreatorSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const moduleAccess = await resolveModuleAccess();
        setAccess(moduleAccess);

        // Auto-redirect only when there is exactly one destination and no
        // staff-side choices (staff always has the creator picker available).
        if (!moduleAccess.isStaff) {
          const accessibleModules: string[] = [];
          if (moduleAccess.mainApp) accessibleModules.push('/main');
          if (moduleAccess.contentHub) accessibleModules.push('/contenthub');
          if (moduleAccess.creatorsHubOwn) accessibleModules.push('/creatorshub/portal');
          if (accessibleModules.length === 1) {
            navigate(accessibleModules[0], { replace: true });
            return;
          }
        }
      } catch {
        // fallback: show staff modules
        setAccess({ mainApp: true, contentHub: true, creatorsHubOwn: false, isStaff: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  // Deep link from the hub switcher: /apps?creators=1 opens the picker.
  useEffect(() => {
    if (searchParams.get('creators') === '1' && access?.isStaff) {
      openCreatorPicker();
      searchParams.delete('creators');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access]);

  const openCreatorPicker = () => {
    setCreatorPickerOpen(true);
    if (creators === null) {
      influencerService.getAll()
        .then(data => setCreators(data.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))))
        .catch(() => setCreators([]));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f4f0] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-[#15140F] animate-spin" />
      </div>
    );
  }

  const showCreatorsHub = access?.creatorsHubOwn || access?.isStaff;

  const modules = [
    {
      id: 'main',
      title: 'MOHIT App',
      description: 'Gestão de projetos, faturação e administração',
      icon: LayoutGrid,
      onClick: () => navigate('/main'),
      visible: access?.mainApp,
    },
    {
      id: 'contenthub',
      title: 'Content Hub',
      description: 'Gestão e aprovação de conteúdo para redes sociais',
      icon: FileText,
      onClick: () => navigate('/contenthub'),
      visible: access?.contentHub,
    },
    {
      id: 'creatorshub',
      title: 'Creators Hub',
      description: access?.creatorsHubOwn
        ? 'Portal para influencers e criadores de conteúdo'
        : 'Entrar no portal de um criador em modo de visualização',
      icon: Users,
      onClick: () => (access?.creatorsHubOwn ? navigate('/creatorshub/portal') : openCreatorPicker()),
      visible: showCreatorsHub,
    },
  ].filter(m => m.visible);

  const filteredCreators = (creators || []).filter(c =>
    !creatorSearch.trim() ||
    (c.full_name || '').toLowerCase().includes(creatorSearch.toLowerCase()) ||
    ((c as { company_name?: string }).company_name || '').toLowerCase().includes(creatorSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#f5f4f0] flex flex-col relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="absolute top-[-15%] right-[-10%] w-[500px] h-[500px] rounded-full bg-[#D4FF00] opacity-[0.08] blur-[100px] pointer-events-none" />
      <div className="absolute bottom-[-15%] left-[-8%] w-[400px] h-[400px] rounded-full bg-[#FF3366] opacity-[0.05] blur-[100px] pointer-events-none" />

      {/* Header */}
      <header className="relative px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#15140F] flex items-center justify-center">
            <span className="text-white font-extrabold text-xs tracking-wider">M</span>
          </div>
          <span className="text-lg font-extrabold text-[#15140F] tracking-tight">MOHIT</span>
        </div>
        <button
          onClick={() => signOut()}
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-700 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span className="hidden sm:inline">Sair</span>
        </button>
      </header>

      {/* Main */}
      <main className="relative flex-1 flex items-center justify-center px-4 pb-20">
        <div className="w-full max-w-4xl">
          <div className="animate-view-in text-center mb-12">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-[#15140F] tracking-tight mb-2">
              Bem-vindo
            </h1>
            <p className="text-gray-500 text-sm">
              Escolhe o módulo onde queres trabalhar.
            </p>
          </div>

          <div className={`stagger-children grid gap-5 ${modules.length === 1 ? 'grid-cols-1 max-w-sm mx-auto' : modules.length === 2 ? 'grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
            {modules.map(mod => (
              <button
                key={mod.id}
                onClick={mod.onClick}
                className="group relative bg-[#15140F] rounded-3xl p-7 text-left transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-black/20 active:scale-[0.98]"
              >
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/[0.06] to-transparent pointer-events-none" />
                <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-[#D4FF00]/[0.08] to-transparent pointer-events-none" />
                <div className="relative">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center mb-5 group-hover:bg-[#D4FF00]/20 transition-colors">
                    <mod.icon className="w-5 h-5 text-white group-hover:text-[#D4FF00] transition-colors" />
                  </div>
                  <h2 className="text-white font-bold text-lg mb-1.5 tracking-tight">
                    {mod.title}
                  </h2>
                  <p className="text-white/50 text-sm leading-relaxed">
                    {mod.description}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </main>

      {/* Creator picker (staff view-as) */}
      {creatorPickerOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setCreatorPickerOpen(false)}
        >
          <div
            className="bg-white rounded-3xl shadow-float w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 pt-5 pb-3">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Escolhe o criador</h3>
                <p className="text-xs text-gray-500 mt-0.5">Vais entrar no Creators Hub em modo de visualização</p>
              </div>
              <button
                onClick={() => setCreatorPickerOpen(false)}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 pb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={creatorSearch}
                  onChange={e => setCreatorSearch(e.target.value)}
                  placeholder="Procurar criador..."
                  autoFocus
                  className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-3 pb-4">
              {creators === null ? (
                <div className="py-10 text-center">
                  <RefreshCw className="w-6 h-6 text-gray-300 animate-spin mx-auto" />
                </div>
              ) : filteredCreators.length === 0 ? (
                <p className="py-10 text-center text-sm text-gray-400">Nenhum criador encontrado.</p>
              ) : (
                filteredCreators.map(c => (
                  <button
                    key={c.id}
                    onClick={() => navigate(`/creatorshub/portal?as=${c.id}`)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                      {(c.full_name || '?').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{c.full_name}</p>
                      {(c as { company_name?: string }).company_name && (
                        <p className="text-xs text-gray-400 truncate">{(c as { company_name?: string }).company_name}</p>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="relative text-center pb-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-gray-300 font-medium">
          Mohit Entertainment &copy; {new Date().getFullYear()}
        </p>
      </footer>
    </div>
  );
}
